import type { Dash } from '@vp/shared-types';

/** Geometry shared by all backends and SVG export for paths, arrows and dashes. */

export function arrowSize(strokeWidth: number): number {
  // proportional to the line so arrows look right at any zoom level
  return Math.max(0.5, strokeWidth * 4.5);
}

/**
 * Arrow head triangle at the end (`atEnd`) or start of a polyline.
 * Returns [tipX, tipY, leftX, leftY, rightX, rightY] and the base point.
 */
export function arrowHead(points: number[], atEnd: boolean, strokeWidth: number): { tri: number[]; base: [number, number] } | null {
  const n = points.length;
  if (n < 4) return null;
  let tx: number, ty: number, px: number, py: number;
  if (atEnd) {
    tx = points[n - 2];
    ty = points[n - 1];
    let i = n - 4;
    px = points[i];
    py = points[i + 1];
    while (i > 0 && px === tx && py === ty) {
      i -= 2;
      px = points[i];
      py = points[i + 1];
    }
  } else {
    tx = points[0];
    ty = points[1];
    let i = 2;
    px = points[i];
    py = points[i + 1];
    while (i < n - 2 && px === tx && py === ty) {
      i += 2;
      px = points[i];
      py = points[i + 1];
    }
  }
  const dx = tx - px;
  const dy = ty - py;
  const len = Math.hypot(dx, dy);
  if (len === 0) return null;
  const ux = dx / len;
  const uy = dy / len;
  const size = Math.min(arrowSize(strokeWidth), len);
  const half = size * 0.45;
  const bx = tx - ux * size;
  const by = ty - uy * size;
  return { tri: [tx, ty, bx - uy * half, by + ux * half, bx + uy * half, by - ux * half], base: [bx, by] };
}

/** Polyline with ends pulled back to arrow bases so strokes don't poke through arrow tips. */
export function trimForArrows(points: number[], start: boolean, end: boolean, strokeWidth: number): number[] {
  if (!start && !end) return points;
  const out = points.slice();
  if (end) {
    const a = arrowHead(points, true, strokeWidth);
    if (a) {
      out[out.length - 2] = a.base[0];
      out[out.length - 1] = a.base[1];
    }
  }
  if (start) {
    const a = arrowHead(points, false, strokeWidth);
    if (a) {
      out[0] = a.base[0];
      out[1] = a.base[1];
    }
  }
  return out;
}

/** Dash pattern in world units (scales with stroke width), as used by Canvas 2D `setLineDash`. */
export function dashPattern(dash: Dash, width: number): number[] {
  const w = Math.max(width, 0.5);
  if (dash === 'dashed') return [w * 4, w * 2.5];
  if (dash === 'fine') return [w * 1.2, w * 1.2];
  if (dash === 'dotted') return [0, w * 2];
  return [];
}

/** WebGL stroke shader dash kind (see webgl/shaders.ts). */
export function dashKind(dash: Dash): number {
  if (dash === 'solid') return 0;
  if (dash === 'dashed') return 1;
  if (dash === 'dotted') return 2;
  return 3;
}

/** Split a polyline into dash segments (each a polyline) following `pattern`. Dotted = zero-length dashes. */
export function dashPolyline(points: number[], pattern: number[], closed: boolean): number[][] {
  const pts = closed ? [...points, points[0], points[1]] : points;
  if (pattern.length === 0) return [pts];
  const out: number[][] = [];
  let idx = 0;
  let remain = pattern[0];
  let on = true;
  let cur: number[] = [pts[0], pts[1]];
  for (let i = 2; i < pts.length; i += 2) {
    let x0 = pts[i - 2];
    let y0 = pts[i - 1];
    const x1 = pts[i];
    const y1 = pts[i + 1];
    let segLen = Math.hypot(x1 - x0, y1 - y0);
    while (segLen > 0) {
      if (remain <= segLen) {
        const t = remain / segLen;
        const mx = x0 + (x1 - x0) * t;
        const my = y0 + (y1 - y0) * t;
        if (on) {
          cur.push(mx, my);
          out.push(cur);
        }
        cur = [mx, my];
        x0 = mx;
        y0 = my;
        segLen -= remain;
        on = !on;
        idx = (idx + 1) % pattern.length;
        remain = pattern[idx];
        if (out.length > 200000) return out;
      } else {
        remain -= segLen;
        if (on) cur.push(x1, y1);
        segLen = 0;
      }
    }
    if (!on) cur = [x1, y1];
  }
  if (on && cur.length >= 4) out.push(cur);
  else if (on && remain === 0 && cur.length === 2) out.push([cur[0], cur[1], cur[0], cur[1]]); // trailing dot
  return out;
}

/** Flattened outline of a shape primitive (closed polygon) — used for dashes/hit-tests/export. */
export function shapeOutline(shape: 'rect' | 'ellipse' | 'triangle' | 'diamond', x: number, y: number, w: number, h: number, radius = 0, segments = 64): number[] {
  switch (shape) {
    case 'rect': {
      const r = Math.min(radius, w / 2, h / 2);
      if (r <= 0) return [x, y, x + w, y, x + w, y + h, x, y + h];
      const pts: number[] = [];
      const corner = (cx: number, cy: number, a0: number) => {
        for (let i = 0; i <= 6; i++) {
          const a = a0 + (i / 6) * (Math.PI / 2);
          pts.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
        }
      };
      corner(x + w - r, y + r, -Math.PI / 2);
      corner(x + w - r, y + h - r, 0);
      corner(x + r, y + h - r, Math.PI / 2);
      corner(x + r, y + r, Math.PI);
      return pts;
    }
    case 'ellipse': {
      const pts: number[] = [];
      for (let i = 0; i < segments; i++) {
        const a = (i / segments) * Math.PI * 2;
        pts.push(x + w / 2 + (Math.cos(a) * w) / 2, y + h / 2 + (Math.sin(a) * h) / 2);
      }
      return pts;
    }
    case 'triangle':
      return [x + w / 2, y, x + w, y + h, x, y + h];
    case 'diamond':
      return [x + w / 2, y, x + w, y + h / 2, x + w / 2, y + h, x, y + h / 2];
  }
}

export function pointInPolygon(px: number, py: number, poly: number[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 2; i < poly.length; j = i, i += 2) {
    const xi = poly[i];
    const yi = poly[i + 1];
    const xj = poly[j];
    const yj = poly[j + 1];
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function distToPolyline(px: number, py: number, pts: number[], closed: boolean): number {
  let best = Infinity;
  const n = pts.length;
  const lim = closed ? n : n - 2;
  for (let i = 0; i < lim; i += 2) {
    const ax = pts[i];
    const ay = pts[i + 1];
    const bx = pts[(i + 2) % n];
    const by = pts[(i + 3) % n];
    const dx = bx - ax;
    const dy = by - ay;
    const l2 = dx * dx + dy * dy;
    let t = l2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0;
    t = Math.max(0, Math.min(1, t));
    const d = Math.hypot(ax + t * dx - px, ay + t * dy - py);
    if (d < best) best = d;
  }
  return best;
}

/**
 * Flatten a bezier path (nodes [x, y, inX, inY, outX, outY]*) into a polyline.
 * Straight segments (handles on their nodes) stay a single segment; curves get
 * subdivided by their control-polygon length relative to `tolerance`.
 */
export function flattenBezier(nodes: number[], closed: boolean, tolerance = 0.5): number[] {
  const n = Math.floor(nodes.length / 6);
  if (n === 0) return [];
  const out: number[] = [nodes[0], nodes[1]];
  const segs = closed && n > 1 ? n : n - 1;
  for (let s = 0; s < segs; s++) {
    const a = s * 6;
    const b = ((s + 1) % n) * 6;
    const x0 = nodes[a], y0 = nodes[a + 1];
    const x1 = nodes[a + 4], y1 = nodes[a + 5];
    const x2 = nodes[b + 2], y2 = nodes[b + 3];
    const x3 = nodes[b], y3 = nodes[b + 1];
    const straight = x1 === x0 && y1 === y0 && x2 === x3 && y2 === y3;
    if (straight) {
      out.push(x3, y3);
      continue;
    }
    const len = Math.hypot(x1 - x0, y1 - y0) + Math.hypot(x2 - x1, y2 - y1) + Math.hypot(x3 - x2, y3 - y2);
    const steps = Math.max(4, Math.min(96, Math.ceil(Math.sqrt(len / Math.max(tolerance, 1e-9)) * 1.5)));
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const u = 1 - t;
      const k0 = u * u * u, k1 = 3 * u * u * t, k2 = 3 * u * t * t, k3 = t * t * t;
      out.push(k0 * x0 + k1 * x1 + k2 * x2 + k3 * x3, k0 * y0 + k1 * y1 + k2 * y2 + k3 * y3);
    }
  }
  // a closed outline ends where it started; drop the duplicate closing point
  if (closed && out.length >= 4 && out[out.length - 2] === out[0] && out[out.length - 1] === out[1]) out.length -= 2;
  return out;
}
